<template>
  <div
    v-if="isPolygonControlElement"
    class="anchor"
    :style="{
      left: activePosition.x * scale + 'px',
      top: activePosition.y * scale + 'px',
    }"
  >
    <div class="controls" :style="controlPosition">
      <!-- Draw polygon -->
      <button
        v-if="shouldShowDraw"
        class="btn-float"
        title="Draw polygon"
        @click.stop="startPolygon"
      >
        <fa :icon="['fal', 'draw-polygon']" />
      </button>

      <!-- Delete polygon -->
      <button
        v-if="shouldShowDelete"
        class="btn-float"
        title="Delete all polygons"
        @click.stop="deletePolygon"
      >
        <fa :icon="['fal', 'trash']" />
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { ObjectId } from "bson";

export default {
  props: {
    scale: {
      type: Number,
      default: 1,
    },
  },

  computed: {
    ...mapState("draggable", [
      "activeElement",
      "activeElementType",
      "markerIndex",
    ]),

    ...mapState({
      isDrawing: (state) => state.draggable.isDrawing,
    }),

    isPolygonControlElement() {
      if (!this.activeElement) return false;

      if (!["marker", "tag", "dot"].includes(this.activeElementType))
        return false;

      return !!this.activePosition;
    },

    activePosition() {
      if (!this.activeElement) return null;

      if (
        this.activeElementType === "marker" &&
        Array.isArray(this.activeElement.markers)
      ) {
        return this.activeElement.markers[this.markerIndex] || null;
      }

      if (this.activeElementType === "tag" && this.activeElement.pos) {
        return this.activeElement.pos;
      }

      if (this.activeElementType === "dot" && this.activeElement.marker) {
        return this.activeElement.marker;
      }

      return null;
    },

    activeTailPosition() {
      if (!this.activeElement) return null;

      if (
        this.activeElementType === "marker" &&
        Array.isArray(this.activeElement.markers)
      ) {
        const marker = this.activeElement.markers[this.markerIndex];
        return marker ? marker.tailPosition : null;
      }

      if (this.activeElementType === "tag") {
        return this.activeElement.tailPosition;
      }

      if (this.activeElementType === "dot" && this.activeElement.marker) {
        return this.activeElement.marker.tailPosition;
      }

      return null;
    },

    /** Show “draw polygon” if marker has no polygon */
    shouldShowDraw() {
      return !this.isDrawing;
    },

    /** Show delete if polygon exists */
    shouldShowDelete() {
      return (
        this.isPolygonControlElement &&
        this.activeElement.polygons &&
        this.activeElement.polygons.length > 0 &&
        !this.isDrawing
      );
    },

    controlPosition() {
      const pos = this.activeTailPosition;

      const isBottom = [
        "bottom-center",
        "bottom-left",
        "bottom-right",
      ].includes(pos);
      const isTop = ["top-center", "top-left", "top-right"].includes(pos);
      const isLeft = pos === "left-center";
      const isRight = pos === "right-center";

      if (isBottom) {
        return { top: "1rem", transform: "translate(-50%, 0%)" };
      }
      if (isTop) {
        return { bottom: "1rem", transform: "translate(-50%, 0%)" };
      }
      if (isLeft) {
        return { right: "1rem", transform: "translate(0%, -50%)" };
      }
      if (isRight) {
        return { left: "1rem", transform: "translate(0%, -50%)" };
      }

      return { transform: "translate(-120%)" };
    },
  },

  methods: {
    ensurePolygon() {
      if (!Array.isArray(this.activeElement.polygons)) {
        this.$set(this.activeElement, "polygons", []);
      }

      let previousPolygonStyle = {
        fill: "rgba(0,0,0,0)",
        stroke: "rgba(255,255,255,0)",
        strokeWidth: 8,
        dropShadow: false,
      };

      if (this.activeElement.polygons.length > 0) {
        const { fill, stroke, strokeWidth, dropShadow } =
          this.activeElement.polygons[this.activeElement.polygons.length - 1];

        previousPolygonStyle.fill = fill;
        previousPolygonStyle.stroke = stroke;
        previousPolygonStyle.strokeWidth = strokeWidth;
        previousPolygonStyle.dropShadow = dropShadow;
      }

      const id = new ObjectId().toString();
      const polygon = {
        ...previousPolygonStyle,
        _id: id,
        points: [],
      };

      this.activeElement.polygons.push(polygon);
      return id;
    },

    startPolygon() {
      if (!this.activeElement) return;
      const polygonId = this.ensurePolygon();
      this.$store.commit("draggable/startPolygon", polygonId);
    },

    deletePolygon() {
      if (!this.activeElement) return;
      this.$store.commit("draggable/deletePolygon", this.activeElement._id);
    },
  },
};
</script>

<style lang="scss" scoped>
.anchor {
  position: absolute;
  z-index: 100;
}

.controls {
  display: flex;
  gap: 8px;
  position: absolute;
  width: fit-content;

  .btn-float {
    appearance: none;
    aspect-ratio: 1;
    border: none;
    border-radius: 50%;
    font-size: 16px;
    white-space: nowrap;
    width: 30px;
  }
}
</style>
